import { useState, useEffect, useCallback } from "react";
import { Clock, Zap, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";

function formatTime(seconds) {
  if (seconds <= 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

export default function TrialBanner() {
  const { auth, session, setSession } = useAppContext();
  const nav = useNavigate();
  const lang = localStorage.getItem("pigeon-lang") || "en";
  const { t } = useTranslation(lang);
  const [trial, setTrial] = useState(null);
  const [remaining, setRemaining] = useState(-1);
  const [showExpired, setShowExpired] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!auth?.jwt) return;
    try {
      const data = await api.getTrialStatus(auth.jwt);
      setTrial(data);
      if (data.is_trial) {
        setRemaining(data.seconds_remaining);
        if (data.trial_expired) setShowExpired(true);
      }
    } catch {}
  }, [auth?.jwt]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Local countdown ticker
  useEffect(() => {
    if (remaining <= 0 || !trial?.is_trial) return;
    const timer = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          setShowExpired(true);
          clearInterval(timer);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [trial?.is_trial, remaining > 0]);

  if (!session?.hubId || !trial?.is_trial || dismissed || session?.isPitch) return null;

  const urgent = remaining < 7200; // < 2 hours
  const critical = remaining < 1800; // < 30 min

  // Expired overlay
  if (showExpired) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="trial-expired-overlay">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Clock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">{t("trial_expired_title")}</h2>
          <p className="text-sm text-slate-500 mb-6 leading-relaxed">{t("trial_expired_desc")}</p>
          <button
            data-testid="trial-upgrade-btn"
            onClick={() => { setShowExpired(false); setSession(null); nav("/landing"); }}
            className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-black transition-all"
          >
            <Zap className="w-4 h-4 inline mr-2" />
            {t("trial_upgrade_cta")}
          </button>
          <button
            onClick={() => { setShowExpired(false); setSession(null); nav("/portal"); }}
            className="w-full mt-3 py-3 text-slate-400 text-xs font-bold hover:text-slate-600 transition-colors"
          >
            {t("trial_back_portal")}
          </button>
        </div>
      </div>
    );
  }

  // Countdown banner
  return (
    <div
      data-testid="trial-countdown-banner"
      className={`fixed top-0 left-0 right-0 z-[60] flex items-center justify-between px-4 py-2 text-xs font-bold transition-colors ${
        critical ? "bg-red-600 text-white" : urgent ? "bg-amber-500 text-white" : "bg-slate-900 text-slate-300"
      }`}
    >
      <div className="flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" />
        <span>
          {t("trial_remaining")}: <span className="font-mono font-black text-white">{formatTime(remaining)}</span>
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          data-testid="trial-banner-upgrade"
          onClick={() => nav("/landing")}
          className={`px-3 py-1 rounded-lg text-xs font-black transition-all ${
            critical ? "bg-white text-red-600 hover:bg-red-50" : urgent ? "bg-white text-amber-600 hover:bg-amber-50" : "bg-red-600 text-white hover:bg-red-500"
          }`}
        >
          {t("trial_upgrade_short")}
        </button>
        <button onClick={() => setDismissed(true)} className="opacity-60 hover:opacity-100 transition-opacity">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
